import _jsx from "@babel/runtime/helpers/jsx";
import PropTypes from 'prop-types';
import React, { memo, useCallback, useEffect, useRef, useState, useMemo } from 'react';
import clsx from 'clsx';
import _get from 'lodash/get';
import _debounce from 'lodash/debounce';
import _isEqual from 'lodash/isEqual';
import { smoothScrollByPosition, smoothScroll } from '@atc/dom-fns';
import Tab from '../Tab/index.js';
import NavItem from '../NavItem/index.js';
import NavWrap from '../NavWrap/index.js';
import Nav from '../Nav/index.js';
import Glyphicon from '../Glyphicon/index.js';
import Icon from '../Icon/index.js';
import { TabNavBarContext } from '../TabNavBarContainer/TabNavBarContainer.js';
const ComponentWithContext = Component => props => props.isNav ? /*#__PURE__*/_jsx(TabNavBarContext.Consumer, {}, void 0, function (_temp) {
  let {
    activeKey,
    navItems,
    defaultActiveKey,
    topOffset
  } = _temp === void 0 ? {} : _temp;
  return /*#__PURE__*/React.createElement(Component, Object.assign({}, props, {
    activeKey: activeKey,
    navItems: navItems,
    defaultActiveKey: defaultActiveKey,
    topOffset: topOffset
  }));
}) : /*#__PURE__*/React.createElement(Component, props);
const useStateWithCallback = initialState => {
  const [stateValue, setState] = useState(initialState);
  const callbackRef = useRef(null);
  const setStateCallback = useCallback((state, cb) => {
    callbackRef.current = cb;
    setState(prevState => ({
      ...prevState,
      ...state
    }));
  }, []);
  useEffect(() => {
    if (callbackRef.current) {
      callbackRef.current(stateValue);
      callbackRef.current = null;
    }
  }, [stateValue]);
  return [stateValue, setStateCallback];
};
function TabNavBar(_ref) {
  let {
    additionalContentTab,
    alignment,
    bsStyle = 'tabs',
    children,
    className,
    defaultActiveKey = 0,
    enableSliderAnimation,
    isNav = false,
    navItems = [],
    onSelect = () => {},
    size = 'default',
    topOffset = 0,
    uiContext = 'light',
    ...rest
  } = _ref;
  const initialState = {
    containerOffset: 0,
    containerWidth: 0,
    maxPos: 0,
    currentPos: 0,
    windowWidth: 0,
    activeKey: defaultActiveKey,
    scrollable: false,
    showPrevBtn: false,
    showNextBtn: false,
    isTabClicked: false
  };
  const {
    activeKey: activeKeyProp
  } = rest;
  const navContainer = useRef();
  const sliderContainer = useRef();
  const [state, setState] = useStateWithCallback(initialState);
  const [sliderRect, setSliderRect] = useState({
    left: 0,
    width: 0
  });
  const showHideButtons = useCallback(pos => {
    if (navContainer.current && state.scrollable) {
      setState({
        currentPos: pos,
        showPrevBtn: pos > 0,
        showNextBtn: pos + 2 < state.maxPos
      });
    }
  }, [setState, state]);
  const scrollNavContainerTo = useCallback(function (position) {
    if (position === void 0) {
      position = 0;
    }
    if (navContainer.current) {
      smoothScrollByPosition(position, {
        direction: 'left',
        container: navContainer.current,
        duration: 500
      });
      showHideButtons(position);
    }
  }, [showHideButtons]);
  const getMaxPos = useCallback(() => {
    // position can be from 0 to where the last element is
    // returns 0 if the last element is visible already
    const {
      width: containerWidthForNav
    } = navContainer.current.getBoundingClientRect();
    const {
      x: firstX
    } = navContainer.current?.children[0]?.firstChild?.getBoundingClientRect() ?? {};
    const {
      right: lastRightX
    } = navContainer.current?.children[0]?.lastChild?.getBoundingClientRect() ?? {};
    return Math.ceil(lastRightX - firstX - containerWidthForNav);
  }, []);
  const handleTouchEnd = useCallback(event => {
    const {
      containerOffset
    } = state;
    event.stopPropagation();
    // _get current position from dom after drag
    const scrollableArea = _get(navContainer.current, 'children[0]');
    if (scrollableArea && typeof scrollableArea.getBoundingClientRect === 'function') {
      const {
        x
      } = scrollableArea.getBoundingClientRect();
      let position = x - containerOffset;
      if (position < 0) {
        // position should be always > 0
        position = -position;
      }
      showHideButtons(position);
    }
  }, [showHideButtons, state]);
  const scrollToActiveTab = useCallback(key => {
    const activeKeyForTab = key || activeKeyProp;
    const {
      containerWidth,
      containerOffset,
      currentPos,
      maxPos
    } = state;
    const index = navItems.findIndex(elm => elm.eventKey === activeKeyForTab);
    if (index > -1) {
      const activeItem = _get(navContainer.current, `children[0].children[${index}]`);
      if (activeItem) {
        const {
          x,
          right,
          width
        } = activeItem.getBoundingClientRect();

        // scroll nav container left or right based on location of the new active tab
        let position = 0;
        if (containerWidth >= width) {
          if (x - containerOffset < 0) {
            position = currentPos - containerWidth + right;
            if (position < 0) {
              position = 0;
            }
            scrollNavContainerTo(position);
          } else if (right - containerOffset > containerWidth) {
            position = x - containerOffset + currentPos;
            if (position > maxPos - 2) {
              position = maxPos;
            }
            scrollNavContainerTo(position);
          }
        }
      }
    }
  }, [activeKeyProp, navItems, scrollNavContainerTo, state]);
  const scrollToContentByKey = useCallback(function (key, moveTabs) {
    if (moveTabs === void 0) {
      moveTabs = false;
    }
    if (key) {
      setState({
        activeKey: key,
        isTabClicked: true
      }, () => {
        smoothScroll(key, {
          offset: -topOffset
        });
        if (moveTabs) {
          scrollToActiveTab(key);
        }
      });
    }
  }, [scrollToActiveTab, setState, topOffset]);
  const setDefaultActiveKey = useCallback(() => {
    if (navContainer.current) {
      let initialPos = 0;
      // find index of default active key
      const index = navItems.findIndex(elm => elm.eventKey === defaultActiveKey);

      // if index is 0, initialPosition will be 0 as well
      if (index > 0) {
        const activeItem = _get(navContainer.current, `children[0].children[${index}]`);
        const {
          containerOffset,
          maxPos
        } = state;
        let activeItemX = 0;
        if (activeItem && typeof activeItem.getBoundingClientRect === 'function') {
          const {
            x
          } = activeItem.getBoundingClientRect();
          activeItemX = x;
        }
        initialPos = activeItemX - containerOffset;
        // position should be between 0 and maxPos
        if (initialPos <= 0) {
          initialPos = 0;
        }
        if (initialPos >= maxPos) {
          initialPos = maxPos;
        }
        scrollNavContainerTo(initialPos);
        if (isNav) {
          scrollToContentByKey(defaultActiveKey);
        }
      } else {
        showHideButtons(initialPos);
      }
    }
  }, [defaultActiveKey, isNav, navItems, scrollNavContainerTo, scrollToContentByKey, showHideButtons, state]);
  const setContainerStates = useCallback(_ref2 => {
    let {
      callback
    } = _ref2;
    const {
      maxPos
    } = state;
    if (navContainer.current && typeof navContainer.current.getBoundingClientRect === 'function') {
      const {
        x: containerX,
        width: containerWidthForNav
      } = navContainer.current.getBoundingClientRect();
      const maxPosValue = getMaxPos();
      // update content specs
      setState({
        containerOffset: containerX,
        containerWidth: containerWidthForNav,
        scrollable: maxPos > 0,
        maxPos: maxPosValue,
        windowWidth: window.innerWidth
      }, () => {
        if (callback) {
          callback();
        }
      });
    }
  }, [getMaxPos, setState, state]);
  const updateSliderPosition = useCallback(key => {
    const index = navItems.findIndex(element => element.eventKey === key);
    const navElement = navContainer.current;
    const sliderContainerElement = sliderContainer.current;
    if (index > -1) {
      const activeItem = _get(navElement, `children[0].children[${index}]`);
      if (activeItem) {
        const {
          left,
          width
        } = activeItem.getBoundingClientRect();
        const {
          left: navLeft
        } = sliderContainerElement.getBoundingClientRect();

        // calculate the relative position of slider
        setSliderRect({
          left: left - navLeft,
          width
        });
      }
    }
  }, [navItems]);
  const handleResize = _debounce(() => {
    // mobile ios considered the browser is resized when address bar is hidden by scrolling up (height increases)
    // to prevent tab jumping back to 0 unexpectedly, we'll check window width and ignore height
    if (window.innerWidth !== state.windowWidth) {
      // Need to recalculate max x and width
      scrollNavContainerTo(0);
      setContainerStates({
        callback: () => showHideButtons(0)
      });
    }
  }, 1000);
  const handleScroll = function (action) {
    if (action === void 0) {
      action = 'right';
    }
    const {
      containerWidth,
      currentPos,
      maxPos
    } = state;
    let pos = 0;
    if (action === 'right') {
      // adding a little more to make scrolling smoother
      pos = Math.ceil(currentPos + containerWidth) + 2 >= maxPos ? maxPos : currentPos + containerWidth;
    }
    if (action === 'left') {
      pos = currentPos - containerWidth <= 0 ? 0 : currentPos - containerWidth;
    }
    scrollNavContainerTo(pos);
  };
  const handleSelect = (key, event) => {
    if (enableSliderAnimation) {
      updateSliderPosition(key);
    }
    if (isNav) {
      scrollToContentByKey(key, true);
    } else {
      setState({
        activeKey: key
      }, () => scrollToActiveTab(key));
    }
    onSelect(key, event);
  };
  const updateActiveKey = useCallback(() => {
    const {
      activeKey,
      isTabClicked
    } = state;
    const newActiveKey = activeKeyProp;
    if (!isTabClicked) {
      if (newActiveKey !== activeKey) {
        setState({
          activeKey: newActiveKey
        });
        scrollToActiveTab();
      }
    } else if (newActiveKey === activeKey) {
      setState({
        isTabClicked: false
      });
    }
  }, [activeKeyProp, scrollToActiveTab, setState, state]);
  const debouncedUpdate = useMemo(() => _debounce((containerOffset, clientX, callback) => {
    if (containerOffset !== clientX) {
      setContainerStates({
        callback
      });
    }
  }, 100),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  []);
  useEffect(() => {
    if (enableSliderAnimation) {
      updateSliderPosition(activeKeyProp || defaultActiveKey);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enableSliderAnimation]);
  useEffect(() => {
    const navContainerRef = navContainer.current;
    if (window && navContainerRef) {
      setContainerStates({
        callback: setDefaultActiveKey
      });
      navContainerRef.addEventListener('touchend', handleTouchEnd, {
        passive: true
      });
    }
    let resizeObserver;
    if (typeof window !== 'undefined' && 'ResizeObserver' in window && navContainerRef) {
      resizeObserver = new ResizeObserver(handleResize); // Using ResizeObserver to handle resizing
      resizeObserver.observe(navContainerRef);
    }
    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      if (navContainerRef) {
        // mobile devices handler
        navContainerRef.removeEventListener('touchend', handleTouchEnd);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (navContainer.current) {
      // update in case the component is re-rendered with content
      if (state.containerOffset !== navContainer.current.getBoundingClientRect().x) {
        debouncedUpdate(state.containerOffset, navContainer.current.getBoundingClientRect().x, setDefaultActiveKey);
      }

      // when TabNavBar is used as a in-page navigation,
      // update activeKey to match the content that's in viewport
      if (isNav) {
        updateActiveKey();
      }
    }
  }, [isNav, navContainer, setContainerStates, setDefaultActiveKey, state.containerOffset, updateActiveKey, debouncedUpdate]);
  const renderButton = function (direction) {
    if (direction === void 0) {
      direction = 'left';
    }
    const isLeft = direction === 'left';
    const props = {
      className: clsx('padding-vertical-3 cursor-pointer display-inline-block', {
        'hide-button': isLeft ? !state.showPrevBtn : !state.showNextBtn,
        'text-gray': uiContext === 'light',
        'text-gray-light': uiContext === 'dark',
        'padding-left-1 padding-right-2': isLeft,
        'padding-left-2 padding-right-1': !isLeft
      }),
      glyph: isLeft ? 'menu-left' : 'menu-right',
      onClick: handleScroll(direction),
      key: `button-${direction}`
    };
    return /*#__PURE__*/React.createElement(Glyphicon, props);
  };
  const renderNavItems = () => navItems.map((_ref3, index) => {
    let {
      label,
      href,
      icon,
      ...last
    } = _ref3;
    const renderIcons = !!icon && bsStyle === 'tabs' && size === 'xl';
    return /*#__PURE__*/React.createElement(NavItem, Object.assign({}, last, {
      href: href,
      key: `nav-item${index}`
    }), renderIcons && /*#__PURE__*/_jsx(Icon, {
      height: 24,
      icon: icon,
      iconSet: "tabs"
    }), label);
  });
  const renderNavChildren = () => React.Children.map(children, child => /*#__PURE__*/_jsx(NavWrap, {}, void 0, child));
  const renderTabSlider = () => /*#__PURE__*/React.createElement("div", {
    "data-cmp": "tabNavBarSlider",
    ref: sliderContainer,
    className: "nav-tab-slider",
    style: {
      '--tab-slider-left': `${sliderRect.left}px`,
      '--tab-slider-width': `${sliderRect.width}px`
    }
  });
  return /*#__PURE__*/React.createElement(Tab.Container, Object.assign({
    "data-cmp": "tabNavBar",
    id: "tabNavBar",
    className: clsx('nav-tabs-container', className, {
      'tabs-slider-animation': enableSliderAnimation
    }),
    activeKey: state.activeKey,
    onSelect: handleSelect
  }, rest), /*#__PURE__*/_jsx("div", {}, void 0, /*#__PURE__*/_jsx("div", {
    className: additionalContentTab && 'display-flex'
  }, void 0, /*#__PURE__*/_jsx("div", {
    className: `nav-${bsStyle}-items-wrapper display-flex align-items-center`
  }, void 0, state.scrollable ? renderButton('left') : null, /*#__PURE__*/React.createElement("div", {
    className: `nav-wrapper nav-tabs-${uiContext} nav-size-${size} nav-style-${bsStyle}`,
    ref: navContainer,
    key: "nav-container"
  }, /*#__PURE__*/_jsx(Nav, {
    className: alignment === 'left' && 'justify-content-start',
    bsStyle: bsStyle
  }, void 0, renderNavItems(), isNav && children && renderNavChildren()), enableSliderAnimation && renderTabSlider()), state.scrollable ? renderButton('right') : null), additionalContentTab), !isNav && children));
}
export default ComponentWithContext(/*#__PURE__*/memo(TabNavBar, (oldProps, newProps) => _isEqual(newProps.navItems, oldProps.navItems) && _isEqual(newProps.onSelect, oldProps.onSelect) && _isEqual(newProps.children, oldProps.children)));